import * as React from "react";
import {
  Box,
  BoxProps,
  IconButton,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import { EditorContent, useEditor } from "@tiptap/react";
import { runRichTextCommand } from "./richTextCommands";
import { richTextExtensions } from "./richTextExtensions";
import {
  SiFormatBold,
  SiFormatItalic,
  SiFormatListBulleted,
  SiFormatListNumbered,
  SiMinus,
} from "@medstonetech/slate-icons";
import { isRichTextEmpty } from "./summaryContentToHtml";

type RichTextProps = {
  value?: string;
  defaultValue?: string;
  onChange?: (html: string) => void;
  onBlur?: () => void;
  isDisabled?: boolean;
  placeholder?: string;
  minH?: string | number;
  h?: string | number;
  width?: string | number;
  className?: string;
} & Omit<BoxProps, "onChange" | "onBlur" | "value" | "defaultValue">;

const RichTextInner = (
  props: RichTextProps,
  ref: React.ForwardedRef<HTMLDivElement>
) => {
  const {
    value,
    defaultValue,
    onChange,
    onBlur,
    isDisabled = false,
    placeholder,
    minH,
    h,
    width,
    className,
    ...boxProps
  } = props;

  const styles = useMultiStyleConfig("RichText", {});

  const initialContent = value ?? defaultValue ?? "";

  const editor = useEditor({
    extensions: richTextExtensions,
    content: initialContent,
    editable: !isDisabled,
    onUpdate: ({ editor: activeEditor }) => {
      const html = activeEditor.getHTML();
      const next = isRichTextEmpty(html) ? "" : html;
      onChange?.(next);
    },
    onBlur: () => {
      onBlur?.();
    },
  });

  React.useEffect(() => {
    if (!editor) {
      return;
    }
    editor.setEditable(!isDisabled, false);
  }, [editor, isDisabled]);

  React.useEffect(() => {
    if (!editor || value === undefined) {
      return;
    }
    const current = editor.getHTML();
    const normalizedCurrent = isRichTextEmpty(current) ? "" : current;
    const normalizedValue = isRichTextEmpty(value) ? "" : value;
    if (normalizedValue !== normalizedCurrent) {
      editor.commands.setContent(value || "", false);
    }
  }, [editor, value]);

  const toolbarButtonProps = (isActive: boolean) => ({
    variant: "ghost" as const,
    size: "sm" as const,
    isDisabled,
    color: isActive ? "blue.500" : undefined,
    bg: isActive ? "gray.100" : undefined,
  });

  return (
    <Box
      ref={ref}
      className={className}
      __css={styles.root}
      minH={minH}
      h={h}
      width={width}
      aria-label={placeholder}
      {...boxProps}
    >
      <Box __css={styles.toolbar}>
        <IconButton
          aria-label="Bold"
          icon={<SiFormatBold />}
          onClick={() => {
            runRichTextCommand(editor, (chain) => chain.focus().toggleBold());
          }}
          {...toolbarButtonProps(editor?.isActive("bold") ?? false)}
        />
        <IconButton
          aria-label="Italic"
          icon={<SiFormatItalic />}
          onClick={() => {
            runRichTextCommand(editor, (chain) => chain.focus().toggleItalic());
          }}
          {...toolbarButtonProps(editor?.isActive("italic") ?? false)}
        />
        <IconButton
          aria-label="Bullet list"
          icon={<SiFormatListBulleted />}
          onClick={() => {
            runRichTextCommand(editor, (chain) =>
              chain.focus().toggleBulletList()
            );
          }}
          {...toolbarButtonProps(editor?.isActive("bulletList") ?? false)}
        />
        <IconButton
          aria-label="Numbered list"
          icon={<SiFormatListNumbered />}
          onClick={() => {
            runRichTextCommand(editor, (chain) =>
              chain.focus().toggleOrderedList()
            );
          }}
          {...toolbarButtonProps(editor?.isActive("orderedList") ?? false)}
        />
        <IconButton
          aria-label="Horizontal rule"
          icon={<SiMinus />}
          onClick={() => {
            runRichTextCommand(editor, (chain) =>
              chain.focus().setHorizontalRule()
            );
          }}
          {...toolbarButtonProps(false)}
        />
      </Box>
      <Box __css={styles.content} flex="1" minH={0} overflow="auto">
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

const RichText = React.forwardRef(RichTextInner);
RichText.displayName = "RichText";

export { RichText };
export type { RichTextProps };
